/* Atelier Care — storefront components. */
(() => {
  'use strict';

  const config = JSON.parse(
    document.getElementById('ThemeConfig')?.textContent || '{}'
  );

  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');

  const behavior = () =>
    reduceMotion.matches || document.body.dataset.motion === 'false'
      ? 'instant'
      : 'smooth';

  const parse = html =>
    new DOMParser().parseFromString(html, 'text/html');

  const fail = (root, message) => {
    const element = root.querySelector('.form-error');

    if (element) {
      element.textContent = message || config.error;
      element.hidden = false;
    }
  };

  const status = text => {
    const element = document.getElementById('CartStatus');
    if (element) element.textContent = text;
  };

  const endpoint = path =>
    path.endsWith('.js') ? path : `${path}.js`;

  const define = (name, component) => {
    if (!customElements.get(name)) {
      customElements.define(name, component);
    }
  };

  const isArabic = () =>
    document.documentElement.lang.toLowerCase().startsWith('ar');

  class Component extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.setup?.();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.cleanup?.();
    }

    on(target, type, callback) {
      target?.addEventListener(type, callback, {
        signal: this.abort.signal
      });
    }
  }

  class QuantityInput extends Component {
    setup() {
      this.on(this, 'click', event => {
        const button = event.target.closest('[data-step]');
        const input = this.querySelector('input');

        if (!button || !input) return;

        const step = Number(input.step) || 1;
        const minimum = Number(input.min) || 1;
        const maximum = input.max ? Number(input.max) : Infinity;

        input.value = Math.min(
          maximum,
          Math.max(
            minimum,
            (Number(input.value) || minimum) +
              Number(button.dataset.step) * step
          )
        );

        input.dispatchEvent(
          new Event('change', { bubbles: true })
        );
      });
    }
  }

  let cartQueue = Promise.resolve();

  const queueCart = action => {
    const next = cartQueue.then(action, action);
    cartQueue = next.catch(() => {});
    return next;
  };

  const cartDrawer = () =>
    document.getElementById('CartDrawer');

  let returnFocus = null;

  /* Open the cart only when the customer requests it. */
  function openCart() {
    const dialog = cartDrawer();

    if (!dialog || typeof dialog.showModal !== 'function') {
      location.href = config.cart;
      return;
    }

    if (!dialog.open) {
      returnFocus = document.activeElement;
      dialog.showModal();
    }
  }

  function updateCartHTML(html) {
    const activeId = document.activeElement?.closest('cart-content')
      ? document.activeElement.id
      : null;

    const notes = [
      ...document.querySelectorAll('cart-content [name=note]')
    ].map(element => ({
      id: element.id,
      value: element.value
    }));

    const documentFragment = parse(html);
    const source = documentFragment.querySelector('cart-content');

    if (!source) throw Error(config.error);

    const drawer = document.getElementById('CartDrawerContent');

    if (drawer) {
      drawer.replaceChildren(source.cloneNode(true));
    }

    const page = document.querySelector('main cart-content');

    if (page) {
      const node = source.cloneNode(true);

      node.querySelectorAll('[id]').forEach(element => {
        element.id = element.id.replace('drawer', 'page');
      });

      node.querySelectorAll('[for]').forEach(element => {
        element.htmlFor = element.htmlFor.replace('drawer', 'page');
      });

      node.querySelector('[name=update]')?.classList.remove('hidden');
      page.replaceWith(node);
    }

    document.querySelectorAll('[data-cart-count]').forEach(element => {
      if (element.tagName !== 'CART-CONTENT') {
        element.textContent = source.dataset.cartCount;
      }
    });

    if (activeId) {
      document.getElementById(activeId)?.focus({
        preventScroll: true
      });
    }

    notes.forEach(note => {
      const element = document.getElementById(note.id);
      if (element) element.value = note.value;
    });

    status(config.updated);
  }

  async function fetchCartView(url, options = {}) {
    const { json = false, ...fetchOptions } = options;
    const controller = new AbortController();

    const timer = setTimeout(() => {
      controller.abort();
    }, 8000);

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal
      });

      if (!response.ok) throw Error(config.error);

      return json
        ? await response.json()
        : await response.text();
    } finally {
      clearTimeout(timer);
    }
  }

  async function refreshCart() {
    const url = new URL(config.cart, location.origin);
    url.searchParams.set('section_id', 'cart-content');

    updateCartHTML(await fetchCartView(url));
  }

  async function postCart(url, body) {
    const response = await fetch(endpoint(url), {
      method: 'POST',
      body,
      credentials: 'same-origin',
      headers: body instanceof FormData
        ? {
            Accept: 'application/json'
          }
        : {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          }
    });

    const data = await response.json();

    if (!response.ok || data.status) {
      throw Error(
        data.description || data.message || config.error
      );
    }

    return data;
  }

  /* Persistent confirmation: no automatic redirect or dismissal. */
  function showAddedMessage(response) {
    const arabic = isArabic();
    const oldToast = document.getElementById('BGCartToast');

    const previousFocus =
      oldToast?.contains(document.activeElement)
        ? null
        : document.activeElement;

    oldToast?.remove();

    const items = Array.isArray(response?.items)
      ? response.items
      : response
        ? [response]
        : [];

    const firstItem = items[0];

    const productName = items.length > 1
      ? (
          arabic
            ? `تمت إضافة ${items.length} منتجات لسلتك`
            : `${items.length} products added to your cart`
        )
      : (
          firstItem?.product_title ||
          firstItem?.title ||
          ''
        );

    const toast = document.createElement('div');
    toast.id = 'BGCartToast';
    toast.dir = arabic ? 'rtl' : 'ltr';
    toast.setAttribute('role', 'region');

    toast.setAttribute(
      'aria-label',
      arabic ? 'تأكيد الإضافة للسلة' : 'Cart confirmation'
    );

    Object.assign(toast.style, {
      position: 'fixed',
      bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: '2147483647',
      width: '420px',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: '75dvh',
      overflowY: 'auto',
      boxSizing: 'border-box',
      padding: '22px',
      border: '1px solid rgba(0, 0, 123, 0.15)',
      borderTop: '5px solid #00007b',
      borderRadius: '18px',
      background: '#ffffff',
      color: '#00007b',
      boxShadow: '0 12px 60px rgba(0, 0, 0, 0.28)',
      fontFamily: 'inherit',
      textAlign: 'start',
      pointerEvents: 'auto'
    });

    function dismissToast() {
      const focusInside = toast.contains(document.activeElement);
      toast.remove();

      if (focusInside && previousFocus?.isConnected) {
        previousFocus.focus({ preventScroll: true });
      }
    }

    const headingRow = document.createElement('div');

    Object.assign(headingRow.style, {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    });

    const check = document.createElement('span');
    check.textContent = '✓';
    check.setAttribute('aria-hidden', 'true');

    Object.assign(check.style, {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: '0 0 48px',
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      background: '#00007b',
      color: '#ffffff',
      fontSize: '28px',
      fontWeight: '700',
      lineHeight: '1'
    });

    const heading = document.createElement('p');

    heading.textContent = arabic
      ? 'اتضاف للسلة بنجاح!'
      : 'Added to your cart!';

    Object.assign(heading.style, {
      flex: '1',
      minWidth: '0',
      margin: '0',
      fontFamily: 'inherit',
      fontSize: '19px',
      fontWeight: '700',
      lineHeight: '1.4',
      color: '#00007b'
    });

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.textContent = '×';

    closeButton.setAttribute(
      'aria-label',
      arabic ? 'إغلاق التأكيد' : 'Dismiss confirmation'
    );

    Object.assign(closeButton.style, {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: '0 0 44px',
      width: '44px',
      height: '44px',
      padding: '0',
      border: '0',
      borderRadius: '50%',
      background: '#f5f5fc',
      color: '#00007b',
      fontSize: '26px',
      lineHeight: '1',
      cursor: 'pointer'
    });

    closeButton.addEventListener('click', dismissToast);
    headingRow.append(check, heading, closeButton);

    const description = document.createElement('p');
    description.textContent = productName;

    Object.assign(description.style, {
      margin: '14px 0 0',
      color: '#45455b',
      fontFamily: 'inherit',
      fontSize: '14px',
      fontWeight: '500',
      lineHeight: '1.6',
      overflowWrap: 'anywhere'
    });

    const actions = document.createElement('div');

    Object.assign(actions.style, {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: '10px',
      marginTop: '20px'
    });

    const viewCart = document.createElement('a');
    viewCart.href = config.cart;
    viewCart.textContent = arabic ? 'عرض السلة' : 'View cart';

    const continueButton = document.createElement('button');
    continueButton.type = 'button';

    continueButton.textContent = arabic
      ? 'كمل تسوق'
      : 'Keep shopping';

    continueButton.addEventListener('click', dismissToast);

    [viewCart, continueButton].forEach(button => {
      Object.assign(button.style, {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '0',
        minHeight: '48px',
        margin: '0',
        padding: '10px 8px',
        border: '1px solid #00007b',
        borderRadius: '10px',
        boxSizing: 'border-box',
        fontFamily: 'inherit',
        fontSize: '14px',
        fontWeight: '700',
        lineHeight: '1.4',
        textAlign: 'center',
        textDecoration: 'none',
        cursor: 'pointer'
      });
    });

    Object.assign(viewCart.style, {
      background: '#00007b',
      color: '#ffffff'
    });

    Object.assign(continueButton.style, {
      background: '#ffffff',
      color: '#00007b'
    });

    actions.append(viewCart, continueButton);
    toast.append(headingRow);

    if (productName) toast.append(description);

    toast.append(actions);

    toast.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        dismissToast();
      }
    });

    document.body.appendChild(toast);

setTimeout(() => {
  dismissToast();
}, 1000);

    status(
      arabic
        ? `تمت الإضافة للسلة بنجاح. ${productName}`
        : `Added to your cart. ${productName}`
    );
  }

  async function afterAdd(response) {
    showAddedMessage(response);

    try {
      await refreshCart();
    } catch (error) {
      try {
        const cart = await fetchCartView(endpoint(config.cart), {
          json: true,
          headers: {
            Accept: 'application/json'
          }
        });

        document.querySelectorAll('[data-cart-count]').forEach(element => {
          if (element.tagName !== 'CART-CONTENT') {
            element.textContent = cart.item_count;
          }
        });
      } catch (refreshError) {
        console.warn(
          'Item added, but the cart display could not refresh.',
          refreshError
        );
      }
    }

    status(isArabic() ? 'تمت الإضافة للسلة' : 'Added to cart');

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: response
      })
    );
  }

  class ProductForm extends Component {
    setup() {
      this.on(this, 'submit', async event => {
        const form = event.target;

        if (!(form instanceof HTMLFormElement)) return;
        if (event.submitter?.name === 'checkout') return;

        event.preventDefault();

        if (this.busy || !form.reportValidity()) return;

        this.busy = true;

        const data = new FormData(form);
        const buttons = [
          ...form.querySelectorAll('button[type=submit]')
        ];

        const disabledStates = buttons.map(button => button.disabled);

        buttons.forEach(button => {
          button.disabled = true;
        });

        form.setAttribute('aria-busy', 'true');

        const error = this.querySelector('.form-error');
        if (error) error.hidden = true;

        try {
          await queueCart(async () => {
            const response = await postCart(config.add, data);
            await afterAdd(response);
          });
        } catch (error) {
          fail(this, error.message);
        } finally {
          this.busy = false;
          form.removeAttribute('aria-busy');

          buttons.forEach((button, index) => {
            button.disabled = disabledStates[index];
          });
        }
      });
    }
  }

  class CartContent extends Component {
    setup() {
      this.on(this, 'change', event => {
        if (event.target.matches('[data-cart-quantity]')) {
          this.change(
            event.target.dataset.key,
            Number(event.target.value)
          );
        }
      });

      this.on(this, 'click', event => {
        const link = event.target.closest('[data-cart-remove]');

        if (link) {
          event.preventDefault();
          this.change(link.dataset.key, 0);
        }
      });
    }

    async change(key, quantity) {
      if (
        this.busy ||
        !Number.isInteger(quantity) ||
        quantity < 0
      ) return;

      this.busy = true;

      const inputs = [
        ...this.querySelectorAll('input,button,textarea')
      ];

      const states = inputs.map(element => element.disabled);
      const note = this.querySelector('[name=note]')?.value;

      inputs.forEach(element => {
        element.disabled = true;
      });

      this.setAttribute('aria-busy', 'true');

      try {
        await queueCart(async () => {
          await postCart(
            config.change,
            JSON.stringify({
              id: key,
              quantity
            })
          );

          await refreshCart();

          document.querySelectorAll('cart-content [name=note]')
            .forEach(element => {
              if (note !== undefined) element.value = note;
            });
        });
      } catch (error) {
        fail(this, error.message);
      } finally {
        this.busy = false;

        inputs.forEach((element, index) => {
          element.disabled = states[index];
        });

        this.removeAttribute('aria-busy');
      }
    }
  }

  class ScrollRail extends Component {
    setup() {
      this.rail = this.querySelector('[data-rail]');
      if (!this.rail) return;

      this.on(this, 'click', event => {
        const button = event.target.closest(
          '[data-next],[data-previous]'
        );

        if (!button) return;

        const rtl = getComputedStyle(this.rail).direction === 'rtl';

        const delta =
          (button.hasAttribute('data-next') ? 1 : -1) *
          (rtl ? -1 : 1);

        this.rail.scrollBy({
          left: delta * this.rail.clientWidth * 0.85,
          behavior: behavior()
        });
      });

      this.rail.tabIndex = 0;

      this.on(this.rail, 'keydown', event => {
        if (
          event.target !== this.rail ||
          !['ArrowLeft', 'ArrowRight'].includes(event.key)
        ) return;

        event.preventDefault();

        this.rail.scrollBy({
          left:
            (event.key === 'ArrowRight' ? 1 : -1) *
            this.rail.clientWidth * 0.65,
          behavior: behavior()
        });
      });
    }
  }

  class HeroSlideshow extends Component {
    setup() {
      this.track = this.querySelector('[data-rail]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.index = 0;
      this.paused = false;

      if (!this.track || !this.slides.length) return;

      this.on(this, 'click', event => {
        const dot = event.target.closest('[data-slide-index]');

        if (dot) {
          this.go(Number(dot.dataset.slideIndex));
        }

        const pause = event.target.closest('[data-pause]');

        if (pause) {
          this.paused = !this.paused;

          pause.setAttribute(
            'aria-pressed',
            String(this.paused)
          );

          pause.textContent = this.paused ? '▶' : 'Ⅱ';
          this.schedule();
        }
      });

      this.on(this.track, 'scroll', () => {
        clearTimeout(this.scrollTimer);

        this.scrollTimer = setTimeout(() => {
          const parent = this.track.getBoundingClientRect();
          let nearest = 0;
          let distance = Infinity;

          this.slides.forEach((slide, index) => {
            const currentDistance = Math.abs(
              slide.getBoundingClientRect().left - parent.left
            );

            if (currentDistance < distance) {
              distance = currentDistance;
              nearest = index;
            }
          });

          this.index = nearest;
          this.updateDots();
        }, 100);
      });

      this.on(this, 'pointerenter', () => {
        this.hovered = true;
        this.schedule();
      });

      this.on(this, 'pointerleave', () => {
        this.hovered = false;
        this.schedule();
      });

      this.on(this, 'focusin', () => {
        this.focused = true;
        this.schedule();
      });

      this.on(this, 'focusout', event => {
        if (!this.contains(event.relatedTarget)) {
          this.focused = false;
          this.schedule();
        }
      });

      this.on(document, 'visibilitychange', () => this.schedule());
      this.on(reduceMotion, 'change', () => this.schedule());

      this.schedule();
    }

    go(index) {
      this.index =
        (index + this.slides.length) % this.slides.length;

      this.track.scrollTo({
        left:
          (getComputedStyle(this.track).direction === 'rtl' ? -1 : 1) *
          this.track.clientWidth *
          this.index,
        behavior: behavior()
      });

      this.updateDots();
    }

    updateDots() {
      this.querySelectorAll('[data-slide-index]')
        .forEach((button, index) => {
          if (index === this.index) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        });
    }

    schedule() {
      clearInterval(this.timer);

      if (
        this.dataset.autoplay !== 'true' ||
        reduceMotion.matches ||
        this.paused ||
        this.hovered ||
        this.focused ||
        document.hidden ||
        this.slides.length < 2
      ) return;

      this.timer = setInterval(
        () => this.go(this.index + 1),
        Number(this.dataset.delay) || 6000
      );
    }

    cleanup() {
      clearInterval(this.timer);
      clearTimeout(this.scrollTimer);
    }
  }

  class MediaGallery extends Component {
    setup() {
      this.on(this, 'click', event => {
        const link = event.target.closest('[data-media-index]');

        if (!link) return;

        event.preventDefault();
        this.select(Number(link.dataset.mediaIndex));
      });
    }

    select(index) {
      const rail = this.querySelector('[data-rail]');
      if (!rail) return;

      rail.scrollTo({
        left:
          (getComputedStyle(rail).direction === 'rtl' ? -1 : 1) *
          rail.clientWidth *
          index,
        behavior: behavior()
      });

      this.querySelectorAll('[data-media-index]')
        .forEach((link, itemIndex) => {
          link.setAttribute(
            'aria-current',
            String(itemIndex === index)
          );
        });

      this.querySelectorAll('video').forEach(video => {
        video.pause();
      });
    }
  }

  class ProductDetail extends Component {
    setup() {
      this.enableVariant();

      this.on(this, 'change', event => {
        if (event.target.matches('[data-option-select]')) {
          this.selectVariant();
        }
      });

      this.watchSticky();

      requestAnimationFrame(() => {
        const id = this.querySelector('[data-selected-media]')
          ?.dataset.selectedMedia;

        const media = [...this.querySelectorAll('[data-media]')];

        const index = media.findIndex(item => {
          return item.dataset.media === id;
        });

        if (index > 0) {
          this.querySelector('media-gallery')?.select(index);
        }
      });
    }

    enableVariant() {
      const input = this.querySelector('[data-variant-input]');

      if (input && input.value) {
        input.disabled = false;
      }
    }

    watchSticky() {
      this.observer?.disconnect();

      const button = this.querySelector('[data-main-atc]');
      const sticky = this.querySelector('.sticky-atc');

      if (
        !button ||
        !sticky ||
        !('IntersectionObserver' in window)
      ) return;

      this.observer = new IntersectionObserver(entries => {
        const box = entries[0].boundingClientRect;

        sticky.hidden =
          entries[0].isIntersecting || box.top > 0;

        document.body.style.paddingBottom = sticky.hidden
          ? ''
          : `${sticky.offsetHeight}px`;
      }, {
        threshold: 0
      });

      this.observer.observe(button);
    }

    async selectVariant() {
      this.request?.abort();
      this.request = new AbortController();

      const selections = [
        ...this.querySelectorAll('[data-option-select]')
      ].map(select => select.value);

      const activeId = document.activeElement?.id;
      const oldInfo = this.querySelector('[data-product-info]');
      const quantity = this.querySelector('[name=quantity]')?.value;

      this.querySelectorAll('[type=submit]').forEach(button => {
        button.disabled = true;
      });

      this.setAttribute('aria-busy', 'true');

      const url = new URL(this.dataset.url, location.origin);
      url.searchParams.set('option_values', selections.join(','));
      url.searchParams.set('section_id', this.dataset.section);

      try {
        const response = await fetch(url, {
          signal: this.request.signal,
          credentials: 'same-origin'
        });

        if (!response.ok) throw Error(config.error);

        const documentFragment = parse(await response.text());
        const next = documentFragment.querySelector('product-detail');

        if (!next) throw Error(config.error);

        const newInfo = next.querySelector('[data-product-info]');

        oldInfo.replaceWith(newInfo);
        this.enableVariant();

        const oldSticky = this.querySelector('.sticky-atc');
        const newSticky = next.querySelector('.sticky-atc');

        if (oldSticky && newSticky) {
          oldSticky.replaceWith(newSticky);
        }

        const input = this.querySelector('[name=quantity]');

        if (input && quantity) {
          const minimum = Number(input.min) || 1;
          const step = Number(input.step) || 1;
          const maximum = input.max
            ? Number(input.max)
            : Infinity;

          input.value = Math.min(
            maximum,
            Math.max(
              minimum,
              minimum +
                Math.round((Number(quantity) - minimum) / step) * step
            )
          );
        }

        const variant = this.querySelector('.js-variant-id')?.value;
        const publicUrl = new URL(this.dataset.url, location.origin);

        if (variant) {
          publicUrl.searchParams.set('variant', variant);
        } else {
          publicUrl.searchParams.set(
            'option_values',
            selections.join(',')
          );
        }

        history.replaceState({}, '', publicUrl);

        const mediaId = next.querySelector('[data-selected-media]')
          ?.dataset.selectedMedia;

        if (mediaId) {
          const media = [...this.querySelectorAll('[data-media]')];

          const index = media.findIndex(item => {
            return item.dataset.media === mediaId;
          });

          if (index >= 0) {
            this.querySelector('media-gallery')?.select(index);
          }
        }

        if (activeId) {
          document.getElementById(activeId)?.focus({
            preventScroll: true
          });
        }

        this.watchSticky();
        window.Shopify?.PaymentButton?.init?.();

        document.dispatchEvent(
          new CustomEvent('product:variant-change', {
            detail: {
              variantId: variant,
              sectionId: this.dataset.section
            }
          })
        );
      } catch (error) {
        if (error.name !== 'AbortError') {
          fail(this, error.message);
        }
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    cleanup() {
      this.request?.abort();
      this.observer?.disconnect();
      document.body.style.paddingBottom = '';
    }
  }

  class Recommendations extends Component {
    setup() {
      if (
        this.querySelector('.product-card') ||
        !this.dataset.url
      ) return;

      this.observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          this.observer.disconnect();
          this.load();
        }
      }, {
        rootMargin: '300px'
      });

      this.observer.observe(this);
    }

    async load() {
      try {
        const response = await fetch(this.dataset.url, {
          signal: this.abort.signal
        });

        if (!response.ok) return;

        const documentFragment = parse(await response.text());

        const content = documentFragment.querySelector(
          'product-recommendations'
        );

        if (content) {
          this.replaceChildren(...content.childNodes);
        }
      } catch {}
    }

    cleanup() {
      this.observer?.disconnect();
    }
  }

  class BundleForm extends Component {
    setup() {
      this.on(this, 'change', () => this.total());

      this.on(this, 'click', event => {
        if (event.target.closest('[data-bundle-add]')) {
          this.add();
        }
      });

      this.total();
    }

    total() {
      const inputs = [
        ...this.querySelectorAll('[data-bundle-variant]:checked')
      ];

      const cents = inputs.reduce((sum, input) => {
        return sum + Number(input.dataset.price);
      }, 0);

      const total = this.querySelector('[data-bundle-total]');

      if (total) {
        total.textContent = new Intl.NumberFormat(
          this.dataset.locale,
          {
            style: 'currency',
            currency: this.dataset.currency
          }
        ).format(cents / 100);
      }

      const button = this.querySelector('[data-bundle-add]');

      if (button) {
        button.disabled = inputs.length === 0 || this.busy;
      }
    }

    async add() {
      if (this.busy) return;

      const items = [
        ...this.querySelectorAll('[data-bundle-variant]:checked')
      ].map(input => ({
        id: Number(input.dataset.bundleVariant),
        quantity: 1
      }));

      if (!items.length) return;

      this.busy = true;
      this.total();

      this.querySelectorAll('input').forEach(input => {
        input.disabled = true;
      });

      try {
        await queueCart(async () => {
          const response = await postCart(
            config.add,
            JSON.stringify({ items })
          );

          await afterAdd(response);
        });
      } catch (error) {
        fail(this, error.message);
      } finally {
        this.busy = false;

        this.querySelectorAll('input').forEach(input => {
          input.disabled = false;
        });

        this.total();
      }
    }
  }

  const savedKey = 'atelier-care-saved-products';

  function saved() {
    try {
      return JSON.parse(localStorage.getItem(savedKey) || '[]')
        .filter(handle =>
          typeof handle === 'string' &&
          /^[a-z0-9-]+$/.test(handle)
        )
        .slice(0, 50);
    } catch {
      return [];
    }
  }

  function refreshHearts() {
    const handles = saved();

    document.querySelectorAll('[data-wishlist]').forEach(button => {
      button.setAttribute(
        'aria-pressed',
        String(handles.includes(button.dataset.wishlist))
      );
    });
  }

  class WishlistGrid extends Component {
    setup() {
      this.on(document, 'wishlist:changed', () => {
        this.querySelectorAll('.product-card').forEach(card => {
          if (!saved().includes(card.dataset.productHandle)) {
            card.remove();
          }
        });

        if (!saved().length) {
          this.querySelector('[data-wishlist-status]').textContent =
            config.wishlistEmpty;
        }
      });

      this.load();
    }

    async load() {
      const handles = saved();
      const grid = this.querySelector('[data-wishlist-grid]');
      const message = this.querySelector('[data-wishlist-status]');

      if (!handles.length) {
        message.textContent = config.wishlistEmpty;
        return;
      }

      let failed = false;
      const cards = new Array(handles.length);
      let cursor = 0;

      const worker = async () => {
        while (cursor < handles.length) {
          const index = cursor++;

          try {
            const root = config.root.endsWith('/')
              ? config.root
              : config.root + '/';

            const response = await fetch(
              `${root}products/${encodeURIComponent(handles[index])}?section_id=wishlist-item`,
              {
                signal: this.abort.signal
              }
            );

            if (!response.ok) continue;

            cards[index] = parse(await response.text())
              .querySelector('.product-card');
          } catch {
            failed = true;
          }
        }
      };

      await Promise.all(
        Array.from(
          { length: Math.min(4, handles.length) },
          worker
        )
      );

      if (!this.isConnected) return;

      grid.replaceChildren(...cards.filter(Boolean));

      message.textContent = failed
        ? config.error
        : cards.some(Boolean)
          ? ''
          : config.wishlistEmpty;

      refreshHearts();
    }
  }

  define('bundle-form', BundleForm);
  define('quantity-input', QuantityInput);
  define('product-form', ProductForm);
  define('cart-content', CartContent);
  define('scroll-rail', ScrollRail);
  define('hero-slideshow', HeroSlideshow);
  define('media-gallery', MediaGallery);
  define('product-detail', ProductDetail);
  define('product-recommendations', Recommendations);
  define('wishlist-grid', WishlistGrid);

  document.documentElement.classList.add('js');

  document.addEventListener('pointerover', event => {
    if (
      !matchMedia('(hover:hover) and (min-width:1000px)').matches
    ) return;

    const template = event.target.closest('.product-card')
      ?.querySelector('template[data-secondary-image]');

    if (template) {
      template.replaceWith(template.content.cloneNode(true));
    }
  });

  document.addEventListener('click', event => {
    const cart = event.target.closest('[data-open-cart]');

    if (cart && cartDrawer()) {
      event.preventDefault();

      document.getElementById('BGCartToast')?.remove();

      openCart();

      queueCart(refreshCart).catch(() => {
        location.href = config.cart;
      });
    }

    const close = event.target.closest('[data-close-dialog]');

    if (close) {
      close.closest('dialog')?.close();
    }

    const save = event.target.closest('[data-wishlist]');

    if (save) {
      const handle = save.dataset.wishlist;
      let list = saved();

      list = list.includes(handle)
        ? list.filter(item => item !== handle)
        : [...list, handle].slice(-50);

      try {
        localStorage.setItem(savedKey, JSON.stringify(list));
        refreshHearts();

        document.dispatchEvent(
          new Event('wishlist:changed')
        );
      } catch {
        status(config.error);
      }
    }

    const navigation = document.querySelector('.navigation');

    if (
      navigation?.open &&
      !navigation.contains(event.target) &&
      matchMedia('(max-width:999px)').matches
    ) {
      navigation.open = false;
    }
  });

  cartDrawer()?.addEventListener('click', event => {
    if (event.target === cartDrawer()) {
      const rect = event.target.getBoundingClientRect();

      if (
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom
      ) {
        event.target.close();
      }
    }
  });

  cartDrawer()?.addEventListener('close', () => {
    if (returnFocus?.isConnected) {
      returnFocus.focus();
    }
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      document.querySelectorAll(
        '.navigation[open],.nav-group[open]'
      ).forEach(element => {
        element.open = false;
        element.querySelector('summary')?.focus();
      });
    }
  });

  document.addEventListener(
    'shopify:section:load',
    refreshHearts
  );

  document.addEventListener('shopify:block:select', event => {
    const slide = event.target.closest('[data-slide]');
    const carousel = slide?.closest('hero-slideshow');

    if (carousel) {
      carousel.paused = true;
      carousel.schedule();
      carousel.go([...carousel.slides].indexOf(slide));
    }
  });

  window.addEventListener('storage', refreshHearts);

  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      document.getElementById('BGCartToast')?.remove();
      queueCart(refreshCart).catch(() => {});
    }
  });

  const compactNav = matchMedia('(max-width: 999px)');

  function adaptNavigation() {
    document.querySelectorAll(
      '.navigation,.filter-panel'
    ).forEach(element => {
      element.open = !compactNav.matches;
    });
  }

  adaptNavigation();

  compactNav.addEventListener('change', adaptNavigation);

  document.addEventListener(
    'shopify:section:load',
    adaptNavigation
  );

  refreshHearts();
})();